import React, { useMemo, useState } from "react";
import AnalysisPanel, { PanelEmpty } from "../AnalysisPanel";
import Pagination from "../../shared/Pagination";
import useClientPagination from "../../../hooks/useClientPagination";
import { getCiReportMarketPositionDetail } from "../../../api/competitiveIntelligence";
import { InferredOccupancy, titleCaseName } from "../../../utils/ciFormat";
import ThinScroll from "../../shared/ThinScroll";
import { PanelTakeaway, fmtRate, fmtDateShort, fmtSignedRate } from "../shared";

const PAGE_SIZE = 15;

// How many competitors to show inline before collapsing behind "See more".
const VISIBLE_COMPETITORS = 3;

// gap_to_you positive = you priced above the competitor → green (you're ahead)
//                     negative = you priced below → red (could raise to match)
const gapTone = (gap) => {
  if (gap == null) return "text-gray-500 dark:text-gray-400";
  if (gap > 0) return "text-emerald-600 dark:text-emerald-400";
  if (gap < 0) return "text-red-600 dark:text-red-400";
  return "text-gray-500 dark:text-gray-400";
};

const MarketPositionDetailBody = ({ rows }) => {
  // Per-row "see more" state: a Set of row indices expanded to show every
  // competitor. Collapsed rows show only the first VISIBLE_COMPETITORS inline.
  const [expandedCompetitorRows, setExpandedCompetitorRows] = useState(() => new Set());

  if (!rows.length) {
    return <PanelEmpty message="No per-date market position data for the selected category." />;
  }

  const toggleAllCompetitors = (index) => {
    setExpandedCompetitorRows((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  return (
    <ThinScroll className="overflow-x-auto responsive-card-table-wrapper">
      <table className="w-full min-w-[900px] text-sm table-fixed responsive-card-table">
        <colgroup>
          <col style={{ width: "12%" }} />
          <col style={{ width: "26%" }} />
          <col style={{ width: "14%" }} />
          <col style={{ width: "12%" }} />
          <col style={{ width: "36%" }} />
        </colgroup>
        <thead className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
          <tr className="border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
            <th className="px-4 py-2.5 text-left font-semibold first:pl-5">Date</th>
            <th className="px-4 py-2.5 text-left font-semibold">Window</th>
            <th className="px-4 py-2.5 text-right font-semibold">Your Rate</th>
            <th className="px-4 py-2.5 text-center font-semibold">Rank</th>
            <th className="px-4 py-2.5 text-left font-semibold last:pr-5">Competitors</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => {
            const competitors = Array.isArray(row.competitors) ? row.competitors : [];
            const showAll = expandedCompetitorRows.has(index);
            const visible = showAll ? competitors : competitors.slice(0, VISIBLE_COMPETITORS);
            const hiddenCount = competitors.length - visible.length;
            return (
              <tr key={`${row.check_in}-${index}`} className="border-b dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition align-top">
                <td data-label="Date" className="px-4 py-2.5 font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap first:pl-5">{fmtDateShort(row.check_in)}</td>
                <td data-label="Window" className="px-4 py-2.5 text-gray-600 dark:text-gray-300 whitespace-nowrap truncate" title={row.date_window_name || ""}>{row.date_window_name || "—"}</td>
                <td data-label="Your Rate" className="px-4 py-2.5 text-right text-gray-900 dark:text-gray-100 font-semibold whitespace-nowrap">{fmtRate(row.your_rate)}</td>
                <td data-label="Rank" className="px-4 py-2.5 text-center whitespace-nowrap">
                  <span className="inline-flex items-center justify-center min-w-[3rem] px-2 py-0.5 rounded-md bg-[#32B1FF]/10 text-[#32B1FF] font-semibold text-xs">
                    {row.your_rank ?? "—"}/{row.total_properties ?? "—"}
                  </span>
                </td>
                {/* Competitors render inline in the column: the first VISIBLE_COMPETITORS by
                    default, with a "See more" button to reveal the rest. Each entry is a
                    single line — name left-aligned (truncated + title-cased), rate /
                    occupancy / gap right-aligned in consistent grid columns, no dot
                    separators, a pipe divider between entries, and a color-coded gap. */}
                <td data-label="Competitors" className="px-4 py-2.5 last:pr-5 align-top rc-title-cell">
                  {competitors.length === 0 ? (
                    <span className="text-xs text-gray-500 dark:text-gray-400">No competitors tracked</span>
                  ) : (
                    <div className="flex flex-col gap-1 w-full">
                      {visible.map((comp, compIndex) => {
                        const notLast = compIndex < visible.length - 1;
                        return (
                          <div
                            key={`${comp.name}-${compIndex}`}
                            className="grid grid-cols-[minmax(0,1fr)_3.5rem_4.5rem_4rem_1rem] items-center gap-x-2 text-xs"
                            title={comp.name || ""}
                          >
                            <span className="min-w-0 truncate font-medium text-gray-700 dark:text-gray-200 pr-1">{titleCaseName(comp.name) || "—"}</span>
                            <span className="text-right text-gray-900 dark:text-gray-100 tabular-nums whitespace-nowrap">{fmtRate(comp.rate)}</span>
                            {comp.occupancy != null ? (
                              <InferredOccupancy value={comp.occupancy} className="text-right text-gray-500 dark:text-gray-400 tabular-nums whitespace-nowrap" />
                            ) : (
                              <span className="text-right text-gray-400 dark:text-gray-500 tabular-nums whitespace-nowrap">—</span>
                            )}
                            <span className={`text-right font-semibold tabular-nums whitespace-nowrap ${gapTone(comp.gap_to_you)}`}>{fmtSignedRate(comp.gap_to_you)}</span>
                            <span className="text-center text-gray-300 dark:text-gray-600" aria-hidden="true">{notLast ? "|" : ""}</span>
                          </div>
                        );
                      })}
                      {hiddenCount > 0 && (
                        <button
                          type="button"
                          onClick={() => toggleAllCompetitors(index)}
                          className="self-start mt-0.5 text-xs text-[#32B1FF] hover:underline font-medium"
                        >
                          See more {hiddenCount} more →
                        </button>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="px-4 py-2 text-[11px] text-gray-500 dark:text-gray-400">
        Gap is signed: <span className="text-emerald-600 dark:text-emerald-400">+$X</span> = you priced above competitor;
        <span className="text-red-600 dark:text-red-400"> −$X</span> = below.
      </div>
    </ThinScroll>
  );
};

const MarketPositionDetailBodyPaginated = ({ rows }) => {
  const sorted = useMemo(
    () => [...rows].sort((rowA, rowB) => (rowA.check_in || "").localeCompare(rowB.check_in || "")),
    [rows]
  );
  const { page, setPage, pageItems, totalPages, totalItems } = useClientPagination(sorted, { pageSize: PAGE_SIZE });
  return (
    <>
      <MarketPositionDetailBody rows={pageItems} />
      {totalPages > 1 && (
        <div className="px-5 pt-1 pb-3 border-t border-gray-200 dark:border-gray-800">
          <Pagination
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
            totalItems={totalItems}
            perPage={PAGE_SIZE}
            label="rows"
          />
        </div>
      )}
    </>
  );
};

const MarketPositionDetailPanel = ({ clientId, categoryCode, say }) => (
  <AnalysisPanel
    id={`market-position-detail-${clientId}-${categoryCode || "all"}`}
    title="Market Position Detail"
    tip="For each date, shows your numeric rank in the comp set and the gap to each competitor by name. Use it to see exactly who is undercutting you and by how much."
    description="Your rank among competitors for each date, with the gap to each property."
    badge={categoryCode ? categoryCode.toUpperCase() : null}
    fetcher={() => getCiReportMarketPositionDetail(clientId, { categoryCode: categoryCode || "rv" })}
    deps={[clientId, categoryCode]}
  >
    {({ data }) => {
      const rows = Array.isArray(data) ? data : (data?.items ?? []);
      return (
        <>
          <PanelTakeaway panelKey="market_position" say={say} />
          <MarketPositionDetailBodyPaginated rows={rows} />
        </>
      );
    }}
  </AnalysisPanel>
);

export default MarketPositionDetailPanel;
